/**
 * 콘솔 메뉴 정본.
 *
 * 출처 두 가지를 합쳤다.
 *   기존 CSMS 화면 정의서(2021-12-16 rev1) 「0. 전체 메뉴 구성」 — 주메뉴 10 / 보조메뉴
 *   대내_CSMS_디자인변경_테스트시나리오_2025_04_29.xlsx — 2025 개편판의 0~2depth
 *
 * 지금까지 콘솔은 화면 열다섯 개를 한 줄로 평평하게 늘어놓고 있었다. 자료의 트리와
 * 견주면 「이력」과 「운영 관리」가 섞여 있고, 무엇이 아직 없는지도 안 보인다.
 *
 * 그래서 트리를 여기 한 곳에 적고 사이드바는 이것을 그린다. 경로는 그대로 둔다 —
 * 메뉴를 묶는 일과 주소를 바꾸는 일을 함께 하면 무엇이 깨졌는지 가려진다.
 *
 * ⚠️ `to` 가 없는 항목은 **아직 화면이 없는 것**이다. 사이드바는 그것을 흐리게 그리고
 * 누를 수 없게 둔다. 빈 링크로 두면 눌러서 빈 화면을 보게 되고, 목록에서 빼 버리면
 * 무엇이 남았는지 아무도 모른다.
 */
export type MenuItem = {
  label: string;
  /** 없으면 미구현. */
  to?: string;
  /** 자료에서의 근거·메모. 미구현 항목이 왜 필요한지 남긴다. */
  note?: string;
};

/**
 * 주메뉴 아이콘 이름(lucide-react). 문자열로 두고 화면이 컴포넌트로 바꾼다 — 이 파일이
 * 그림 라이브러리를 알 필요가 없다.
 */
export type MenuGroup = { label: string; icon: string; items: MenuItem[] };

export const MENU: MenuGroup[] = [
  {
    label: "대시보드",
    icon: "LayoutDashboard",
    items: [{ label: "대시보드", to: "/" }],
  },
  {
    label: "충전인프라",
    icon: "PlugZap",
    items: [
      // 회의문서 260907 「3. 충전기 관제」 — 지금 이 순간의 충전소·충전기 상태를 한눈에.
      // 앞의 「* 」= 목업 기준 작업 완료 표시(사용자 지시 2026-09-11).
      { label: "* 충전기 관제", to: "/infra/monitoring" },
      // 2025 시나리오는 둘을 한 메뉴로 본다(0depth 「충전소/충전기」). 화면 안에서 탭으로 가른다.
      { label: "* 충전소", to: "/infra/stations" },
      { label: "* 충전기", to: "/infra/chargers" },
      { label: "* 제조사/모델", to: "/infra/makers" },
      { label: "* 장애코드", to: "/infra/error-codes" },
      { label: "네트워크", note: "현재 운영 충전기에는 해당 없음" },
      { label: "OCPP 설정", note: "현재 운영 충전기에는 해당 없음" },
      { label: "스마트 차징", note: "현재 운영 충전기에는 해당 없음" },
    ],
  },
  {
    label: "이력",
    icon: "History",
    items: [
      { label: "* 충전/결제 내역", to: "/history/charges" },
      // 로밍은 한 덩어리가 아니다 — 타사 충전은 이력, 요금제는 요금제 아래가 맞다(2025 시나리오).
      { label: "로밍 충전 결제내역", to: "/history/roaming" },
      { label: "* 인증 내역", to: "/history/cert-records" },
      { label: "* 장애발생 내역", to: "/history/faults" },
      { label: "* AS 내역", to: "/history/as" },
      { label: "* CS 내역", to: "/history/cs" },
      // 원격제어 내역은 통신 전문과 별도 화면(사용자 지시 2026-09-11) — KPI + 목록.
      { label: "원격제어 내역", to: "/history/remote-controls" },
      // 「로그 내역」을 따로 두었다가 없앴다. 충전기가 주고받은 전문이 곧 통신 로그라
      // 같은 화면을 가리켰는데, 그러면 어느 쪽을 눌러도 둘 다 켜진 것처럼 보인다
      // (사용자 지적 2026-09-17). 하나로 합치고 이름도 그 뜻으로 적는다.
      { label: "통신 내역(로그)", to: "/history/telegrams" },
      // 주기 작업이 도는지 멈췄는지. 멈춘 것은 워터마크로는 안 보인다 — 멈춘 동안에도
      // 값이 그대로라 아무 신호가 없다. 그래서 실행 자체를 남기고 여기서 본다.
    ],
  },
  {
    label: "통계",
    icon: "BarChart3",
    items: [{ label: "통계", to: "/stats" }],
  },
  {
    label: "정산",
    icon: "Receipt",
    items: [
      // 매출 정산·토스 정산 내역·사업자 월정산은 같은 「정산」 물음의 세 층(건별 → PG 대조 →
      // 월 집계)이라 한 메뉴 안의 탭이다. 갈라 두니 둘이 뭐가 다르냐고 묻게 됐다(2026-10-08).
      { label: "* 정산", to: "/settlement/sales", note: "매출 정산 · 토스 정산 내역 · 사업자 월정산" },
      { label: "정산 집계", to: "/settlement/summary", note: "그룹·기간별 집계" },
      { label: "로밍 정산", to: "/settlement/roaming", note: "받을 것·내줄 것 양방향" },
      { label: "위탁운영 정산", to: "/settlement/apartments", note: "단지별 월간 정산서 조회·확정" },
      // 건별 미수는 충전/결제 내역에서 본다. 여기는 사람 단위 — 회수는 사람에게 한다.
      { label: "* 회원별 미수금", to: "/settlement/unpaid" },
    ],
  },
  {
    label: "회원 관리",
    icon: "Users",
    items: [
      { label: "* 회원 정보", to: "/members/list" },
      { label: "* 단지 관리", to: "/members/apartments", note: "등록 단지 기준 회원 자동매핑·단지 사진" },
      // 카드 발급 자체는 회원 상세에서 한다(사용자 결정 2026-08-31). 이 목록은 그 앞
      // 단계다 — 앱에서 신청이 들어왔는데 재고가 없어 배정을 기다리는 줄을 본다.
      // 목록을 없애면 「누가 신청했는지」를 볼 자리가 사라진다.
      { label: "* 회원카드 발급", to: "/members/cards" },
      // 4-2 재고 풀 — 발급은 회원 상세, 여기는 번호 입고와 잔여수량.
      { label: "* 회원카드 관리", to: "/members/card-stock" },
      { label: "회원 활동", to: "/members/activity" },
      // 동·호는 1세대 1등록이라 이관이 생긴다 — 왜 바뀌었는지 답할 자리가 필요하다(2026-09-23).
      { label: "세대(동·호) 변경 내역", to: "/members/unit-hist" },
    ],
  },
  {
    label: "포인트/쿠폰",
    icon: "Ticket",
    items: [
      { label: "포인트", to: "/rewards/points", note: "적립률 관리·회원 원장" },
      { label: "쿠폰", to: "/rewards/coupons", note: "금액 쿠폰 발급·등록" },
    ],
  },
  {
    label: "운영 관리",
    icon: "Settings2",
    items: [
      // 「사업자」와 「운영그룹」은 같은 표(corp)를 가리키던 두 이름이었다. 2025 시나리오
      // 어휘인 운영그룹으로 합친다 — 같은 화면이 메뉴에 둘로 있으면 어느 쪽이 정본인지
      // 묻게 된다(영업코드도 같은 것이다).
      { label: "운영그룹 · 영업코드", to: "/ops/corp" },
      { label: "요금제", to: "/ops/pricing" },
      { label: "프로모션 요금제", to: "/ops/promotions" },
      { label: "로밍 요금제", to: "/roaming" },
      { label: "차량모델", to: "/ops/car-models" },
      { label: "관리자 계정", to: "/ops/admins" },
      { label: "이벤트로그", to: "/ops/event-logs", note: "관리자 CRUD 감사 로그" },
      { label: "충전기 설치 문의", to: "/ops/setup-requests" },
    ],
  },
  {
    label: "홈페이지 관리",
    icon: "Megaphone",
    items: [
      { label: "* 공지사항", to: "/site/notices", note: "앱 공지 게시판" },
      { label: "* FAQ", to: "/site/faq", note: "앱 자주 묻는 질문" },
      { label: "* 약관", to: "/site/stipulations" },
      { label: "팝업", to: "/site/popups" },
      { label: "요금 안내", to: "/site/price-guide", note: "앱 요금 안내(회원/비회원·공용/비공용)" },
    ],
  },
  {
    label: "설정",
    icon: "Cog",
    items: [
      { label: "계정 설정", to: "/settings/account" },
      { label: "도움말", to: "/settings/help" },
    ],
  },
];

/**
 * 위탁운영사(CONSIGNOR) 계정이 여는 화면. 서버도 같은 범위만 연다(csms-api
 * common/scope.CONSIGNOR_ROUTES) — 여기는 메뉴를 줄이는 것이고 막는 것은 서버다.
 * 상세·편집 화면은 접두사로 따라온다(/infra/stations/:id).
 */
export const CONSIGNOR_PATHS = [
  "/infra/stations", "/infra/chargers",
  "/history/charges", "/history/cert-records", "/history/faults", "/history/as", "/history/cs",
  "/settlement/apartments",
  "/members/list", "/members/apartments",
  "/settings/account", "/settings/help",
];

/** 이 롤이 볼 메뉴. 위탁운영사는 CONSIGNOR_PATHS 만, 나머지 롤은 전체. */
export function menuFor(role: string | null | undefined): MenuGroup[] {
  if (role !== "CONSIGNOR") return MENU;
  return MENU.map((g) => ({ ...g, items: g.items.filter((i) => i.to && CONSIGNOR_PATHS.includes(i.to)) }))
    .filter((g) => g.items.length > 0);
}

/** 이 롤이 이 경로를 여는가. 위탁운영사가 주소창으로 다른 화면을 열면 첫 화면으로 돌린다. */
export function allowsPath(role: string | null | undefined, pathname: string): boolean {
  return role !== "CONSIGNOR" || CONSIGNOR_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

/** 이 경로가 어느 주메뉴에 속하는가. 사이드바가 그 묶음을 펴 둔다. */
export function groupOf(pathname: string): string | null {
  // 긴 경로가 먼저 걸리게 본다. "/" 는 모든 경로의 접두사라 마지막에 맞춘다.
  const candidates = MENU.flatMap((g) => g.items.filter((i) => i.to).map((i) => ({ group: g.label, to: i.to! })))
    .sort((a, b) => b.to.length - a.to.length);
  const hit = candidates.find((c) => (c.to === "/" ? pathname === "/" : pathname.startsWith(c.to)));
  return hit ? hit.group : null;
}

/** 화면이 붙은 메뉴 수 / 정본 트리의 전체 메뉴 수. 문서와 화면이 어긋나면 여기서 보인다. */
export function menuCoverage(): { built: number; total: number } {
  const items = MENU.flatMap((g) => g.items);
  return { built: items.filter((i) => i.to).length, total: items.length };
}
